/* WC v0 — the phone→TV WiFi-CREDENTIALS QR payload, and the reader for the
   network list the Hearth advertised. The reset code is a different thing
   entirely and lives in rsCode.js; both are written in the envelope defined by
   pipeEnvelope.js.

   Wire contract: docs/imp-docs/no-network-wifi/QR-WC-FORMAT.md §2 (generation-2
   pipe envelope, type WC, version 0):

     |WC|0|<ssid>|<password>|

   Empty password = open network. This format is frozen; changes are a version
   bump, never a mutation. */

import { escapeEnvelopeField } from './pipeEnvelope';

export function buildWcCode(ssid, password) {
  return `|WC|0|${escapeEnvelopeField(ssid)}|${escapeEnvelopeField(password)}|`;
}

/* Parses the TV QR's URL fragment: "#ssids=<s1>|<s2>|..." where each SSID is
   URL-encoded (the device encodes spaces as %20, never +).

   Deliberately NOT URLSearchParams: that API percent-decodes values, which
   would destroy the per-SSID encoding BEFORE the pipe split — an SSID
   containing a literal "|" (encoded %7C) would be split in two, and one
   containing "%" would hit a second, corrupting decode. The raw parameter
   value is split on "|" first, then each piece is decoded exactly once.

   Returns [] for an absent/empty fragment; a piece that fails to decode is
   dropped, the rest survive. The page then offers manual entry as needed. */
export function parseSsidsFromHash(hash) {
  const raw = (hash || '').replace(/^#/, '');
  const pair = raw.split('&').find((p) => p.startsWith('ssids='));
  if (!pair) return [];
  const rawList = pair.slice('ssids='.length);
  if (!rawList) return [];
  /* Separator handling (field finding, 13 Aug): a literal "|" separator does
     NOT survive real-world URL transport — phone browsers/share sheets
     normalize it to %7C, which then collides with the per-SSID encoding and
     collapses the whole list into one option. Canonical separator is now ","
     (survives transport; URLEncoder encodes SSID-internal commas as %2C, so
     no collision). Legacy fallbacks keep older TV builds working: raw "|"
     (the original separator), then "%7C" when a browser mangled it — the
     latter is ambiguous with an SSID-internal pipe, so it is only used when
     no raw separator produced a list. */
  const rawPieces = rawList.includes(',')
    ? rawList.split(',')
    /* eslint-disable-next-line no-nested-ternary */
    : rawList.includes('|')
      ? rawList.split('|')
      /* The %7C fallback rescues a LEGACY pipe-separated list that a browser
         percent-encoded. It cannot be told apart from a single SSID whose own
         name contains a pipe — "Cafe|Lounge" reaches us as "Cafe%7CLounge" —
         so a two-part result is genuinely ambiguous and we take the safe
         reading: keep the name whole. Splitting a name that was never a list
         offers the customer networks that do not exist, and the Hearth then
         narrates a not-found failure for a network they can see working.
         Leaving it whole costs them one odd-looking entry they can bypass with
         manual entry. Three or more parts is not ambiguous in practice (an SSID
         with two pipes in it is vanishingly rare), so that still splits. */
      : rawList.split(/%7C/i).length > 2
        ? rawList.split(/%7C/i)
        : [rawList];
  return rawPieces
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch (e) {
        return '';
      }
    })
    .filter((s) => s.length > 0);
}
